import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EXAM_URLS } from "../../api/AdminUrls";
import { post } from "../../services/smartApiService";
import SessionCompleted from "./sessionCompleted";
import { motion } from "framer-motion";
import { changeDateTimeZone } from "../../services/core/CommonService";
import { showAlertAutoClose } from "../../services/notifyService";

const InstructionsPage: React.FC = () => {
  const [traineeDetails, setTraineeDetails] = useState<any>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
  const navigate = useNavigate();

  useEffect(() => {
    let URL = EXAM_URLS.GET_SESSION;
    const subscription = post(URL, {}).subscribe((response) => {
      setTraineeDetails(response.data);
      if (response.data.status === "in_progress") {
        navigate("/exam/exam", { state: { language: selectedLanguage } });
      } else if (response.data.session_status === "active") {
        showAlertAutoClose("Session Not started ...", "warning");
        navigate("/");
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleStartExam = () => {
    let URL = EXAM_URLS.START_EXAM;
    const subscription = post(URL, {
      language_code: selectedLanguage,
    }).subscribe(() => {
      navigate("/exam/exam", { state: { language: selectedLanguage } });
    });
    return () => {
      subscription.unsubscribe();
    };
  };

  const proceedWithExam = () => {
    navigate("/exam/exam", { state: { language: selectedLanguage } });
  };

  if (traineeDetails?.status === "completed") {
    return <SessionCompleted type="completed" />;
  }

  const instructions = [
    "This is a multiple-choice exam.",
    `You have ${traineeDetails?.time_limit} minutes to complete the exam.`,
    "Choose the correct option for each question.",
    'Once you finish the exam, click the "Finish Exam" button.',
    "Make sure to submit the exam before time runs out.",
  ];

  const currentTime = new Date().toLocaleTimeString();

  const details = [
    { label: "Name", value: traineeDetails?.ename || "N/A" },
    { label: "Employee ID", value: traineeDetails?.euserid || "N/A" },
    { label: "Exam Date", value: changeDateTimeZone(traineeDetails?.session_date) || "N/A" },
    { label: "Current Time", value: currentTime || "N/A" },
    { label: "Location", value: traineeDetails?.location || "N/A" },
  ];
  const HindiDetails = [
    { label: "नाम", value: traineeDetails?.ename || "N/A" },
    { label: "कर्मचारी आईडी", value: traineeDetails?.euserid || "N/A" },
    { label: "परीक्षा तिथि", value: changeDateTimeZone(traineeDetails?.session_date) || "N/A" },
    { label: "वर्तमान समय", value: currentTime || "N/A" },
    { label: "स्थान", value: traineeDetails?.location || "N/A" },
  ];
  const hindiInstructions = [
    "यह एक बहुविकल्पीय परीक्षा है।",
    `परीक्षा पूरी करने के लिए आपके पास ${traineeDetails?.time_limit || "अपरिभाषित"} मिनट हैं।`,
    "प्रत्येक प्रश्न के लिए सही विकल्प चुनें।",
    'परीक्षा समाप्त करने के बाद, "परीक्षा समाप्त करें" बटन पर क्लिक करें।',
    "समय समाप्त होने से पहले परीक्षा अवश्य जमा करें।",
  ];

  return (
    <motion.div
      className="instructions-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container is-fluid">
        {/* Language Selection */}
        <motion.div
          className="columns"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="column is-half is-offset-one-quarter">
            <div className="box custom-box p-3">
              <h4 className="title is-5">Select Language</h4>
              <div className="field">
                <div className="control has-icons-left">
                  <div className="select is-fullwidth">
                    <select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                    >
                      <option value="">Please Select</option>
                      <option value="en">English</option>
                      <option value="hi">Hindi</option>
                    </select>
                  </div>
                  <span className="icon is-small is-left">
                    <i className="fas fa-globe"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {selectedLanguage.length > 0 && (
          <>
            <div className="columns is-multiline is-vcentered">
              {/* Left: Trainee Details */}
              <motion.div
                className="column is-12-mobile is-6-tablet"
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <div className="box custom-box">
                  <h3 className="title is-4">
                    {selectedLanguage === "en" ? "Trainee Details" : "प्रशिक्षु विवरण"}
                  </h3>
                  <motion.ul
                    className="content"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    {(selectedLanguage === "en" ? details : HindiDetails).map((detail, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.2 }}
                        className="list-item"
                      >
                        <strong>{detail.label}:</strong> {detail.value}
                      </motion.li>
                    ))}
                  </motion.ul>
                </div>
              </motion.div>

              {/* Right: Exam Instructions */}
              <motion.div
                className="column is-12-mobile is-6-tablet"
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <div className="box custom-box">
                  <h3 className="title is-4">
                    {selectedLanguage === "en" ? "Exam Instructions" : "परीक्षा निर्देश"}
                  </h3>
                  <motion.ul
                    className="content"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    {(selectedLanguage === "en" ? instructions : hindiInstructions).map(
                      (instruction, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.2 }}
                          className="list-item"
                        >
                          {index + 1}. {instruction}
                        </motion.li>
                      )
                    )}
                  </motion.ul>
                </div>
              </motion.div>
            </div>

            {/* Action Buttons */}
            <div className="columns">
              <div className="column is-12">
                {traineeDetails?.status === "pending" && (
                  <motion.button
                    className="button is-success is-fullwidth"
                    onClick={handleStartExam}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <i className="fas fa-play-circle mr-2"></i> Start Exam
                  </motion.button>
                )}
                {traineeDetails?.status === "in_progress" && (
                  <motion.button
                    className="button is-success is-fullwidth"
                    onClick={proceedWithExam}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <i className="fas fa-play-circle mr-2"></i> Proceed Exam
                  </motion.button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default InstructionsPage;
